/**
 * The host-bound half of `cs_item_browser`'s shared utils. The pure half —
 * `asArray`/`formatDate`/`formatFieldDateValues`/`isDateFieldType`/`humanizeDate`/
 * `humanizeFieldDateValues`/`formatSize`/`toTitleCase`/`numberWithCommas`/`isSubClipId`, every one
 * of them a verbatim copy of `cantemo-plugins`' `apps/frontend/src/shared/utils.tsx` — lives in
 * `@cinesys/experience-ui/components` (S2.2 PR C); import those from there, not from here.
 *
 * What's left is `pluginEnabled`, adapted from Cantemo's version (see its own doc comment for
 * what changed and why).
 *
 * The metadata-viewer-only half moved to `@cinesys/experience-ui`'s `MetadataViewer/platform.ts`
 * with that tree (S2.4) — `openSearch`, `getItemId`, `getSelectedIds`,
 * `getMetadataViewerCapabilities` and `setPlayerTime` live only there now. Don't add one back
 * here; two copies of a host capability drift.
 */

/**
 * Cantemo's version reads `window.cs_react.enabled_plugins` (Portal's own plugin registry).
 * There is no plugin registry here — every "plugin" in customer-apps is a flag-gated route, not
 * an installable Portal plugin — so this can't delegate to one either. 15 call sites across 7
 * files (`git grep 'pluginEnabled('`): `Sidebar.tsx` (`cs_collection_browser`, `cs_job_viewer`),
 * `SearchPage.tsx`/`SectionPage.tsx` (`cs_item_styles`, `cs_metadata_viewer` each), `Wrapper.tsx`
 * (`cs_collection_browser`, `cs_metadata_viewer`), `DrillInPage.tsx` (`cs_metadata_viewer` x2,
 * `cs_item_styles`), `ItemBrowserList.tsx` (`cs_item_styles`, `cs_metadata_viewer`,
 * `cs_admin_helper`), `JobView.tsx` (`cs_admin_helper`).
 *
 * Per handle, keyed on whether a REAL backend for it exists in `apps/customer-api` today (not on
 * whether the frontend component was copied — every one of these was copied verbatim regardless):
 * - `cs_collection_browser`: TRUE. `routes/cs_collection_browser/routes.ts` is fully built
 *   (collections/saved-searches CRUD, pin, add-items) and self-gates per-org via its own
 *   `CS_COLLECTION_BROWSER_ENABLED` (404s, doesn't crash, if an org has it off).
 *   `CollectionBrowserTree`'s row links into the shared drill-in view (`/collection/:id`,
 *   `/saved_search/:id` — Monday 12965030446), which `AppRouter.tsx` now routes.
 * - `cs_mapped_collections`: TRUE (Monday 13292999401, iconik-only). Same shape as
 *   `cs_collection_browser` above — `routes/cs_mapped_collections` self-gates per-org via its own
 *   `cs_mapped_collections.enabled` setting, but gracefully (`{enabled: false, storages: []}`,
 *   never a 404/403) rather than hiding the whole rail tab. The rail's own browsing/mutation calls
 *   are `cs_collection_browser`'s existing routes (scoped by a resolved `collectionId`), so an org
 *   with this flag on and `cs_collection_browser` off would see an always-403ing rail — not
 *   enforced here, see docs/decisions.md's 2026-10-14 entry.
 * - `cs_metadata_viewer`: TRUE. `routes/cs_metadata_viewer/index.ts` is fully built and
 *   self-gates the same way (`CS_METADATA_VIEWER_ENABLED`, graceful 404).
 * - `cs_item_styles`: FALSE, deliberately, not a gap. `routes/cs_item_styles` mounts the real
 *   `/cs_item_styles/item_styles` -> `{styles}` contract the frontend actually calls, alongside
 *   `routes/home-dashboard/itemStyles.ts`'s differently-shaped `/home-dashboard/item-styles` ->
 *   `{itemStyles}` route (also mounted) — so this is not a 404-avoidance flag. It stays `false`
 *   because turning the feature on is a separate decision this ticket doesn't make — flip this
 *   case once that go-ahead lands.
 * - `cs_job_viewer`: FALSE. Zero backend routes anywhere (`git grep cs_job_viewer` in
 *   `apps/customer-api/src` is empty) — not "one gap in an otherwise-working feature" the way
 *   collections is, the whole surface is unbacked.
 * - `cs_admin_helper`: FALSE. `HashButton.tsx` calls `window.cs_react_modals
 *   ?.cs_admin_helper_hash_detail?.current?.open(...)` — that modal (Cantemo's
 *   `AdminHelperModal`) was never ported/mounted here, so the button would render but silently
 *   no-op on click.
 *
 * An unrecognized handle defaults to `false` (same safe-by-default posture as before this
 * change) rather than throwing — a future ported feature needs its own case added here, not a
 * silent `true`.
 *
 * Also wired up as `customerAppsHostAdapter.ts`'s `HostAdapter.pluginEnabled` implementation —
 * `JobView.tsx`'s "Admin Helper" button lives in `@cinesys/experience-ui` and can no longer
 * import this function directly, so the adapter delegates straight here rather than keeping a
 * second copy of this switch.
 */
export const pluginEnabled = (pluginHandle: string): boolean => {
  switch (pluginHandle) {
    case "cs_collection_browser":
    case "cs_metadata_viewer":
    case "cs_mapped_collections":
      return true;
    case "cs_item_styles":
    case "cs_job_viewer":
    case "cs_admin_helper":
    default:
      return false;
  }
};
